import { GridFilterModel, GridFilterItem } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import DOMPurify from 'dompurify';
import Papa from 'papaparse';
import { ReactNode } from 'react';

import { ListType, ParentSelectionType, RoutesEnum, ServiceGeoLocationPosition } from '../@types';
import { EMPTY_FIELD_MESSAGE, TAB } from '../constants/common';

dayjs.extend(utc);

export const caseIndependentCompare = (stringA: string, stringB: string) => {
  return stringA.toLocaleUpperCase().includes(stringB.toLocaleUpperCase());
};

export const isString = (value: unknown): value is string => {
  return typeof value === 'string';
};

export const isEmptyString = (value: unknown) => {
  if (value == null) return false;
  return String(value).trim().length === 0;
};

export const isValideStringLength = (value: string, length: number) => {
  const trimmedValue = value.trim();
  return trimmedValue && trimmedValue.length > 0 && trimmedValue.length < length;
};

export const isTextContainValidCharacters = (value: string, regExp?: RegExp) => {
  const result = regExp != null ? regExp.test(value) : /^[a-z0-9 ,.'-]+$/i.test(value);
  return !result;
};

export const validateText = (value: string, text: string, regExp?: RegExp) => {
  let errorText = '';
  if (isEmptyString(value)) {
    errorText = `${text} cannot be empty`;
  } else if (isTextContainValidCharacters(value, regExp)) {
    errorText = `Invalid character or characters included in ${text}`;
  }
  return errorText;
};

export const formatDate = (date: string, defaultValue?: string, displayAsUTC?: boolean) => {
  if (!date) {
    return defaultValue != undefined ? defaultValue : '-';
  }
  if (displayAsUTC) {
    return dayjs.utc(date).format('YYYY-MM-DD HH:mm');
  }
  return dayjs(date).format('YYYY-MM-DD HH:mm');
};

export const formatServiceLogDate = (date: string) => {
  if (!date) {
    return '-';
  }
  return dayjs(date).format('YYYY-MM-DD HH:mm:ss');
};

export const formatDateSimple = (date: string) => {
  if (!date) {
    return '-';
  }
  return dayjs(date).format('YYYY-MM-DD HH:mm');
};

export const formatDateOnly = (date: string) => {
  if (!date) {
    return '-';
  }
  return dayjs(date).format('YYYY-MM-DD');
};

export const formatFriendlyDateTime = (date: string | Date | null | undefined) => {
  if (!date) {
    return '-';
  }
  const parsed = dayjs(date);
  return parsed.isValid() ? parsed.format('D MMM YYYY, h:mm A') : '-';
};

// Date-only sibling of formatFriendlyDateTime. Guards against null/undefined and
// unparsable values (returning '-' rather than the literal "Invalid Date" that a
// bare `new Date(x).toLocaleDateString()` produces for a missing/garbage value).
export const formatFriendlyDate = (date: string | Date | null | undefined) => {
  if (!date) {
    return '-';
  }
  const parsed = dayjs(date);
  return parsed.isValid() ? parsed.format('D MMM YYYY') : '-';
};

export const isDropDownEmpty = (value: string) => {
  // ToDo: value === '0' will remove after whole implementation is done
  if (value === '' || value === '-1' || value === '0') {
    return true;
  } else {
    return false;
  }
};

export const dropdownVerifiedMessage = (value: string, fieldName: string) => {
  return isDropDownEmpty(value) || !value ? `${fieldName} ${EMPTY_FIELD_MESSAGE}` : '';
};

export const getFormattedDate = (date: string) => {
  if (isEmptyString(date)) return undefined;
  else return dayjs(date, 'YYYY-MM-DD HH:mm').format();
};

export const getCurrentTimestamp = () => {
  return new Date().toISOString();
};

export const getCurrentTime = () => {
  return dayjs(new Date()).format('YYYY-MM-DD HH:mm');
};

export const getTimestamp = (date: string) => {
  return dayjs(date, 'YYYY-MM-DD HH:mm').toISOString();
};

/**
 * Serialise a user-picked local date/time for backend fields that are stored
 * as a naive wall-clock string (e.g. a service's scheduledAt / followUpAt).
 *
 * These fields hold whatever string they're given — the BFF reformats the
 * incoming value with `moment(value).format('YYYY-MM-DD HH:mm:ss')` in the
 * server's timezone. Sending a UTC ISO string (`.toISOString()`) therefore
 * shifts the stored value by the local offset: a morning time in an
 * ahead-of-UTC zone (e.g. 09:00 AEST) crosses midnight when converted to UTC,
 * landing the saved date a day behind the one the user picked. Formatting the
 * local wall-clock value directly preserves the chosen calendar date.
 */
export const formatLocalDateTime = (value: dayjs.Dayjs): string => {
  return value.format('YYYY-MM-DD HH:mm:ss');
};

export const getJSTimestamp = (date: string) => {
  return new Date(date).toISOString();
};

export const getLocalDateString = (date: string) => {
  return new Date(date).toLocaleDateString();
};

export const getDateFromTimestamp = (date: string) => {
  if (!date) {
    return '-';
  }
  return dayjs(date).format('YYYY-MM-DD HH:mm');
};

export const emptyFieldValidation = (value: string, text: string) => {
  if (isEmptyString(value)) {
    return text + EMPTY_FIELD_MESSAGE;
  } else {
    return '';
  }
};

export const emptyDropdownValidation = (value: string, text: string) => {
  if (isDropDownEmpty(value)) {
    return text + EMPTY_FIELD_MESSAGE;
  } else {
    return '';
  }
};

export const formatDateTime = (date: string | undefined) => {
  if (!date) {
    return '-';
  }
  return dayjs(date).format('YYYY-MM-DD hh:mm:ss');
};

export const formatShortDate = (date: string | undefined) => {
  if (!date) {
    return '-';
  }
  return dayjs(date).format('YYYY-MM-DD');
};

const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60 * MS_PER_SECOND;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;
const MS_PER_WEEK = 7 * MS_PER_DAY;
// Month and year constants are approximations (30d / 365d). Preserves the
// prior moment.duration(ms) semantics without needing a duration library.
const MS_PER_MONTH = 30 * MS_PER_DAY;
const MS_PER_YEAR = 365 * MS_PER_DAY;

export const calculateDateTimeDuration = (date: string) => {
  const loggedDateTime = new Date(date);
  const currentDateTime = new Date();
  const timeDiff = currentDateTime.getTime() - loggedDateTime.getTime();

  const years = Math.floor(timeDiff / MS_PER_YEAR);
  const months = Math.floor((timeDiff % MS_PER_YEAR) / MS_PER_MONTH);
  const weeks = Math.floor((timeDiff % MS_PER_MONTH) / MS_PER_WEEK);
  const days = Math.floor((timeDiff % MS_PER_WEEK) / MS_PER_DAY);
  const hours = Math.floor((timeDiff % MS_PER_DAY) / MS_PER_HOUR);
  const minutes = Math.floor((timeDiff % MS_PER_HOUR) / MS_PER_MINUTE);
  const seconds = Math.floor((timeDiff % MS_PER_MINUTE) / MS_PER_SECOND);

  if (years === 0 && months === 0 && weeks === 0 && days === 0 && hours === 0 && minutes === 0)
    return `+${seconds}s`;
  else if (years === 0 && months === 0 && weeks === 0 && days === 0 && hours === 0)
    return `+${minutes}m`;
  else if (years === 0 && months === 0 && weeks === 0 && days === 0)
    return `+${hours}h ${minutes}m`;
  else if (years === 0 && months === 0 && weeks === 0) return `+${days}d ${hours}h ${minutes}m`;
  else if (years === 0 && months === 0) return `+${weeks}w ${days}d ${hours}h ${minutes}m`;
  else if (years === 0) return `+${months}m ${weeks}w ${days}d ${hours}h ${minutes}m`;
  else return `+${years}y ${months}m ${weeks}w ${days}d ${hours}h ${minutes}m`;
};

export const sanitizeElement = (text: string) =>
  text && text !== '' ? DOMPurify.sanitize(text) : '';

export const isSafeUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url, window.location.origin);
    return (
      parsed.protocol === 'https:' || parsed.protocol === 'http:' || parsed.protocol === 'blob:'
    );
  } catch {
    return false;
  }
};

export const nameValidation = (value: string, text: string) => {
  if (isEmptyString(value)) {
    return text + EMPTY_FIELD_MESSAGE;
  } else {
    return '';
  }
};

export const stripHtmlTags = (html: string): string => {
  const sanitized = sanitizeElement(html);
  const doc = new DOMParser().parseFromString(sanitized, 'text/html');
  return doc.body.textContent || '';
};

export const parseExcelData = (excelDataString: string) => {
  const parsedData = Papa.parse(excelDataString, {
    header: false,
    skipEmptyLines: true,
    delimiter: TAB,
  });
  return parsedData?.data ? parsedData.data : [];
};

export const capitalizeFirstLetter = (input: string) => {
  return input && input !== '' ? input.charAt(0).toUpperCase() + input.slice(1).toLowerCase() : '';
};

export const generateUniqueCode = () => {
  const randomNum = Math.floor(Math.random() * 1000000);
  const base64Encoded = btoa(randomNum.toString());
  return base64Encoded;
};

export const getServiceGeoLocationData = (serviceId: number): ServiceGeoLocationPosition | null => {
  const geoLocationData = localStorage.getItem(RoutesEnum.GeoLocationPositionKey);
  if (geoLocationData) {
    try {
      const geoLocationDataArray: ServiceGeoLocationPosition[] = JSON.parse(geoLocationData);
      const serviceGeoLocationData = geoLocationDataArray.find(
        (locationData: ServiceGeoLocationPosition) => locationData.serviceId === serviceId
      );
      if (serviceGeoLocationData) {
        return serviceGeoLocationData;
      }
    } catch {
      return null;
    }
  }
  return null;
};

export const saveServiceGeoLocationData = (serviceGeoLocationData: ServiceGeoLocationPosition) => {
  const geoLocationData = localStorage.getItem(RoutesEnum.GeoLocationPositionKey);
  if (geoLocationData) {
    try {
      const geoLocationDataArray: ServiceGeoLocationPosition[] = JSON.parse(geoLocationData);
      const existingSeviceGeoLocationDataIndex = geoLocationDataArray.findIndex((locationData) => {
        return locationData.serviceId === serviceGeoLocationData.serviceId;
      });
      if (existingSeviceGeoLocationDataIndex !== -1) {
        geoLocationDataArray.splice(existingSeviceGeoLocationDataIndex, 1);
      }
      geoLocationDataArray.push(serviceGeoLocationData);
      localStorage.setItem(RoutesEnum.GeoLocationPositionKey, JSON.stringify(geoLocationDataArray));
      return;
    } catch {
      // If parsing fails, overwrite with new data
    }
  }
  localStorage.setItem(RoutesEnum.GeoLocationPositionKey, JSON.stringify([serviceGeoLocationData]));
  return;
};

export const convertHyphenTextToReadText = (text: string): string => {
  if (text && text !== '') {
    const words = text.split('-');
    const titleCasedString = words
      .map((word) => {
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
    return titleCasedString;
  }
  return '';
};

export const convertToArray = (arrayData: string) => {
  if (arrayData) {
    return arrayData.split(',');
  }
  return [];
};

export const sleep = async (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export const checkListPagetFilterAvailability = (filters: GridFilterModel) => {
  return filters.items.every(
    (item: GridFilterItem) =>
      item.operator === 'isEmpty' ||
      item.operator === 'isNotEmpty' ||
      !!item.value ||
      (item.field === 'reviewed' && item.value !== undefined) ||
      (item.field === 'clientInvoiced' && item.value !== undefined)
  );
};

export const hasRelativeTimesDatePickerFilter = (pageName: string, field: string) => {
  return (
    (pageName === ListType.ServiceList &&
      ['createdAt', 'updatedAt', 'scheduledDate', 'closedAt', 'followUpDate'].includes(field)) ||
    (pageName === ListType.StructureList && ['createdAt', 'updatedAt'].includes(field)) ||
    (pageName === ListType.PartList && ['createdAt', 'updatedAt', 'warrantyEnd'].includes(field)) ||
    (pageName === ListType.PartMovementList && ['createdAt'].includes(field)) ||
    (pageName === ListType.ShipmentList &&
      ['createdAt', 'updatedAt', 'completedAt', 'arrivalDate'].includes(field))
  );
};

export const getActiveToggleButton = (
  locationId: number,
  selectedParenHierarchy: string | ReactNode
) => {
  if (locationId) {
    return ParentSelectionType.Search;
  } else if (selectedParenHierarchy && typeof selectedParenHierarchy === 'string' && !locationId) {
    return ParentSelectionType.Client;
  } else return ParentSelectionType.None;
};
